import { useCallback, useState, CSSProperties, ReactNode, FC } from 'react';

import { NotificationContext } from '../../shared/contexts/notificationContext';
import { Notification, NotificationProps } from '../../shared/components/ui/Notification';
import { sanitizeVendorMessage } from '../../shared/utils/sanitizeVendorMessage';


interface NotificationItem extends Omit<NotificationProps, 'id' | 'onClose'> {
  id: string;
}

export const NotificationProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const addNotification = useCallback((notification: Omit<NotificationProps, 'id' | 'onClose'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    setNotifications(prev => [...prev, {
      ...notification,
      id,
      message: sanitizeVendorMessage(notification.message),
      title: notification.title
        ? sanitizeVendorMessage(notification.title)
        : notification.title,
    }]);
  }, []);

  const notify = useCallback((props: Omit<NotificationProps, 'id' | 'onClose'>) => {
    addNotification(props);
  }, [addNotification]);

  const success = useCallback((message: string, title?: string) => {
    addNotification({ type: 'success', message, title });
  }, [addNotification]);

  const error = useCallback((message: string, title?: string) => {
    addNotification({ type: 'error', message, title });
  }, [addNotification]);

  const warning = useCallback((message: string, title?: string) => {
    addNotification({ type: 'warning', message, title });
  }, [addNotification]);

  const info = useCallback((message: string, title?: string) => {
    addNotification({ type: 'info', message, title });
  }, [addNotification]);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  return (
    <NotificationContext.Provider value={{ notify, success, error, warning, info, clearAll }}>
      {children}
      
      {notifications.length > 0 && (
        <div className="notification-container" style={notificationContainerStyle}>
          {notifications.map((notification) => (
            <Notification
              key={notification.id}
              {...notification}
              onClose={() => removeNotification(notification.id)}
            />
          ))}
        </div>
      )}
    </NotificationContext.Provider>
  );
};

const notificationContainerStyle: CSSProperties = {
  position: 'fixed',
  bottom: '20px',
  right: '20px',
  zIndex: 999999999999999,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
};